import { Injectable, signal, WritableSignal, effect, untracked } from '@angular/core';
import { SearchLevel, SearchSchoolType, Course, SearchYear } from '../models/search-school.model';
import { Coordinates, FindTutorsRequest, FindTutorsResponse, UserStateEnum } from '../microservice-clients/user';
import { UtilityService } from './utility.service';
import { UserClientService } from './user-client.service';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { GeoService } from './geo.service';
import { SearchFilterQueryParams } from '../models';
import { AuthService } from './auth.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { CourseIcons } from '../models/course.model';
import { FlowRouteService, FlowStorageKeys } from './flow-route.service';

const minRange = 20;
@Injectable({
  providedIn: 'root'
})
export class SearchService {

  schoolTypes: Array<SearchSchoolType> = [];
  schoolTypesReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  selectedSchoolLevel: SearchLevel | null = null;
  selectedLevelId: number | null = null;
  selectedSchoolTypeId: WritableSignal<number | null> = signal(null);
  selectedYearId: number | null = null;
  selectedYear: SearchYear | null = null;
  selectedCourseIds: number[] | null = [];
  selectedCourses: Array<Course> = [];

  popularCourses: Array<Course> = [];
  allCourses: Array<Course> = [];
  availableCourses: Array<Course> = [];

  isOnline: boolean;
  isAtHome: boolean = true;
  allowToShowIsRecommended: boolean = false;
  selectedTutorLevels: string[] = [];

  postCode: string | null = null;
  searchCoordinates: Coordinates;
  showPostCodeError: boolean;
  isSearchDialogCompleted: boolean = false;

  // The random seed used to keep ordering of the same search
  randomSeed: number;

  searchId?: string;
  lastClickedTutorContext?: { tutorId: string; searchId: string; position: number };

  private static readonly CLICK_CONTEXT_STORAGE_KEY = 'tutorCardClickContext';

  searchFilterQueryParams: SearchFilterQueryParams = {};

  storedScrollPosition: number;

  backToScrollView: boolean;

  constructor(
    private utilityService: UtilityService,
    private userClientService: UserClientService,
    private authService: AuthService,
    private geoService: GeoService,
    private router: Router,
    private translationService: TranslateService,
    private flowRouteService: FlowRouteService
  ) {
    this.getSchoolTypes();
    if (this.authService.userDetails?.personalInfo.location) {
      this.postCode = this.authService.userDetails.personalInfo.location.postalCode;
    }
    this.authService.onCurrentUserChange.subscribe(user => {
      this.postCode = user?.personalInfo.location?.postalCode ? user.personalInfo.location.postalCode : undefined;
    })
    this.restoreClickContextFromStorage();
  }

  userStateChange = effect(() => {
    const userState = this.authService.userState();
    if (userState === null) {
      untracked(() => {
        this.clearAll();
      });
    }
  });

  clearAll() {
    this.selectedSchoolLevel = null;
    this.selectedLevelId = null;
    this.selectedSchoolTypeId.update(() => null);
    this.selectedYear = null;
    this.selectedYearId = null;
    this.selectedCourses = [];
    this.selectedCourseIds = [];
    this.selectedTutorLevels = [];

    if (!this.authService.userDetails || this.authService.userDetails?.state === UserStateEnum.New) {
      this.postCode = null;
      this.searchCoordinates = null;
    }
  }

  onLevelSelect(level: SearchLevel) {
    this.selectedYearId = null;
    this.selectedYear = null;

    if (!level || this.selectedSchoolLevel?.id == level.id) {
      this.selectedSchoolLevel = null;
      this.selectedSchoolTypeId.set(null)
      this.selectedLevelId = null
      this.availableCourses = this.allCourses;
      return;
    }
    this.setLevel(level)
  }

  setLevel(level: SearchLevel) {
    this.selectedSchoolLevel = level;
    this.selectedLevelId = level.id
    this.selectedSchoolTypeId.update(() => level._schoolTypeId);
    this.setAvailableCoursesByLevel();
    this.filterSelectedCoursesByAvailableCourses();
  }

  private getSchoolTypes() {
    this.userClientService.getAllSearchSchoolTypesAndCourses().subscribe(resp => {
      this.schoolTypes = resp.schoolTypes.map(schoolType => {
        const updatedLevels = schoolType.levels.map(level => {
          return { ...level, _schoolTypeId: schoolType.id };
        });
        return { ...schoolType, levels: updatedLevels };
      });
      this.setAllCourses();
      if (this.utilityService.isDE) {
        this.sortSchoolLevelsForGermany();
      }
      this.availableCourses = this.allCourses;
      this.schoolTypesReady.next(true);
    })
  }

  setAvailableCoursesByYearCheckbox(year: SearchYear) {
    if (year.id === this.selectedYearId) {
      this.selectedYear = null;
      this.selectedYearId = null;
      this.setAvailableCoursesByLevel()
    }
    else {
      this.selectedYear = year;
      this.selectedYearId = year.id;
      this.availableCourses = [];
      this.createAvailableCourses(this.availableCourses, year.courses)
      this.filterSelectedCoursesByAvailableCourses();
    }
  }

  restoreFromFilterParams(params: SearchFilterQueryParams): void {
    if (params.coursesId) {
      this.selectedCourseIds = (Array.isArray(params.coursesId) ? params.coursesId : [params.coursesId]).map(id => +id);
      this.selectedCourses = this.selectedCourseIds.map(courseId => this.allCourses.find(course => course.id == courseId)).filter((c): c is Course => !!c);
    }

    if (params.levelId && params.selectedSchoolTypeId) {
      const schoolType = this.schoolTypes.find(s => s.id == params.selectedSchoolTypeId);
      const level = schoolType?.levels.find(l => l.id == params.levelId);
      if (level) {
        this.setLevel(level);
        this.selectedYearId = params.yearId ? +params.yearId : this.selectedYearId;
        this.selectedYear = this.selectedSchoolLevel?.years?.find(y => y.id == this.selectedYearId) || this.selectedYear;
      }
    }

    this.postCode = params.postCode || this.postCode;
    this.isOnline = params.online === true || params.online as any === 'true';
    this.isAtHome = params.atHome === true || params.atHome as any === 'true';
    if (!this.isAtHome && !this.isOnline) {
      this.isAtHome = true;
    }
  }

  clearSchoolTypeLevelAndYear() {
    this.selectedSchoolTypeId.set(null);
    this.selectedYearId = null;
    this.selectedYear = null;
    this.selectedLevelId = null;
    this.selectedSchoolLevel = null;
  }

  setAvailableCoursesByYearNgSelect(year: SearchYear) {
    this.availableCourses = [];
    if (year) {
      this.createAvailableCourses(this.availableCourses, year.courses);
      this.selectedYear = year;
      this.selectedYearId = year.id;
    } else {
      this.selectedYear = null;
      this.selectedYearId = null;
      if (this.selectedSchoolLevel) {
        this.setAvailableCoursesByLevel()
      } else {
        this.availableCourses = this.allCourses;
      }
    }
    this.filterSelectedCoursesByAvailableCourses();
  }

  addOrRemoveCourses(course: Course) {
    if (
      this.selectedCourseIds.includes(course.id)) {
      const removeIndex = this.selectedCourseIds.indexOf(course.id)
      this.selectedCourseIds.splice(removeIndex, 1)
      this.selectedCourses.splice(removeIndex, 1)
    } else {
      this.selectedCourseIds = [...this.selectedCourseIds, course.id]
      this.selectedCourses.push(course);
    }
  }

  toggleTutorLevel(level: string) {
    const index = this.selectedTutorLevels.indexOf(level);
    if (index >= 0) {
      this.selectedTutorLevels.splice(index, 1);
    } else {
      this.selectedTutorLevels.push(level);
    }
  }

  isTutorLevelSelected(level: string): boolean {
    return this.selectedTutorLevels.includes(level);
  }

  search(
    offset: number = 0,
  ): Observable<FindTutorsResponse> {

    let tutorSearch: FindTutorsRequest = {
      country: this.utilityService.country,
      offset: offset,
      limit: minRange,
      lessonType: this.searchCoordinates && this.searchCoordinates.longitude && this.searchCoordinates.latitude ? (this.isAtHome || (this.isAtHome && this.isOnline) ? 'AtHome' : 'Online') : 'Online',
      longitude: this.searchCoordinates?.longitude,
      latitude: this.searchCoordinates?.latitude,
      schoolLevelId: this.selectedLevelId,
      schoolYearId: this.selectedYearId,
      randomSeed: this.randomSeed,
      tutorLevels: this.selectedTutorLevels,
      courseIds: undefined,
      schoolTypeId: undefined,
      atHomeRankingFormula: undefined,
      onlineRankingFormula: undefined,
      anonymousId: undefined
    } as FindTutorsRequest;

    if (!this.authService.user) {
      tutorSearch.anonymousId = this.authService.anonymousId;
    }

    if (this.selectedSchoolTypeId() && this.selectedCourseIds?.length > 0) {
      tutorSearch.schoolTypeId = this.selectedSchoolTypeId();
      tutorSearch.courseIds = this.selectedCourseIds;
    } else if (this.selectedCourseIds?.length > 0) {
      tutorSearch.courseIds = this.selectedCourseIds;
    } else if (this.selectedSchoolTypeId()) {
      tutorSearch.schoolTypeId = this.selectedSchoolTypeId();
    } else {
      tutorSearch.schoolTypeId = this.schoolTypes[0].id;
    }

    return this.userClientService.findTutors(tutorSearch).pipe(tap(resp => {
      this.randomSeed = resp.randomSeed;
      if (offset === 0) {
        this.searchId = resp.searchId;
      }
    }))
  }

  // TODO: if/when additional analytics are added, extract related logic to a separate service
  setClickedTutorContext(tutorId: string, position: number): void {
    if (!this.searchId) return;
    const context = { tutorId, searchId: this.searchId, position };
    this.lastClickedTutorContext = context;
    if (this.utilityService.isBrowser()) {
      sessionStorage.setItem(SearchService.CLICK_CONTEXT_STORAGE_KEY, JSON.stringify(context));
    }
  }

  getClickedTutorContext(tutorId: string | number): { searchId: string; position: number } | null {
    const ctx = this.lastClickedTutorContext;
    if (!ctx) return null;
    return ctx.tutorId === String(tutorId) ? { searchId: ctx.searchId, position: ctx.position } : null;
  }

  private restoreClickContextFromStorage(): void {
    if (!this.utilityService.isBrowser()) return;
    try {
      const raw = sessionStorage.getItem(SearchService.CLICK_CONTEXT_STORAGE_KEY);
      if (raw) {
        this.lastClickedTutorContext = JSON.parse(raw);
      }
    } catch { }
  }

  allowShowIsRecommended() {
    if (this.selectedCourseIds.length > 0 ||
      this.selectedSchoolTypeId ||
      this.selectedLevelId ||
      this.searchCoordinates
    ) {
      this.allowToShowIsRecommended = true;
    }
  }

  validatePostCodeAndSetCoordinates() {
    return new Promise(resolve => {
      const postCodeValue = this.postCode.replace(/\s+/g, '');
      if (this.utilityService.isValidPostCode(postCodeValue)) {
        this.postCode = postCodeValue;
        this.geoService.getCoordinatedByZip(this.postCode).subscribe({
          next: (response) => {
            this.showPostCodeError = false;
            this.setSearchCoordinates({ latitude: response.coordinates.lat, longitude: response.coordinates.long });
            resolve(true)
          },
          error: () => {
            this.setSearchCoordinates(null);
            this.showPostCodeError = true;
            resolve(false)
          }
        });
      } else {
        this.showPostCodeError = true;
        this.setSearchCoordinates(null);
        resolve(false)
      }
    });
  }

  setQueryParamObjBasedOnSelection() {
    this.searchFilterQueryParams = {
      ...this.selectedSchoolTypeId() && { selectedSchoolTypeId: this.selectedSchoolTypeId() },
      ...this.selectedCourses?.length && { coursesId: this.selectedCourseIds },
      ...this.selectedLevelId && { levelId: this.selectedLevelId },
      ...this.selectedYearId && { yearId: this.selectedYearId },
      ...(this.isAtHome && this.postCode) && { postCode: this.postCode },
      online: this.isOnline,
      atHome: this.isAtHome,
      ...this.randomSeed && { randomSeed: this.randomSeed },
      ...(this.isAtHome && this.searchCoordinates?.latitude) && { lat: this.searchCoordinates.latitude },
      ...(this.isAtHome && this.searchCoordinates?.longitude) && { lon: this.searchCoordinates.longitude },
      ...this.selectedTutorLevels.length > 0 && { tutorLevels: this.selectedTutorLevels.join(',') },
    };
  }

  navigateToSearch(backToScrollView: boolean = false) {
    this.translationService.get('ROUTES.Misc_tutor_search').subscribe(translation => {
      this.backToScrollView = backToScrollView;
      this.router.navigate([translation], { queryParams: this.searchFilterQueryParams });
    });
  }

  async openOrNavigateToSearch() {
    if (this.isSearchDialogCompleted && this.selectedCourseIds?.length > 0) {
      this.navigateToSearch(false);
    } else {
      await this.flowRouteService.navigateToFlow(FlowStorageKeys.searchFlowReturnTo);
    }
  }

  private setSearchCoordinates(coordinates: Coordinates): void {
    this.searchCoordinates = coordinates ? coordinates : null;
  }

  private setAvailableCoursesByLevel() {
    if (this.selectedSchoolLevel.courses) {
      this.availableCourses = this.selectedSchoolLevel.courses;
    } else {
      this.availableCourses = [];
      this.selectedSchoolLevel.years.forEach(year => {
        this.createAvailableCourses(this.availableCourses, year.courses)
      })
    }
  }

  private setAllCourses() {
    this.schoolTypes.forEach(schoolType => {
      schoolType.levels.forEach(level => {
        if (level.courses) {
          this.createAvailableCourses(this.allCourses, level.courses)
        } else {
          level.years.forEach(year => {
            this.createAvailableCourses(this.allCourses, year.courses)
          })
        }
      })
    })
    this.setSubjectsForHomepage()
  }

  private setSubjectsForHomepage() {
    this.popularCourses = this.allCourses.filter(course => {
      if (course.isPopular) {
        if (this.utilityService.isNL) {
          if (course.id == 7) {
            course.icon = CourseIcons.Comments
          } else if (course.id == 8 || course.id == 28 || course.id == 29 || course.id == 31) {
            course.icon = CourseIcons.Abacus
          } else if (course.id == 26) {
            course.icon = CourseIcons.AtomAlt
          } else if (course.id == 25) {
            course.icon = CourseIcons.Microscope
          }
        } else {
          if (course.id == 41 || course.id == 42 || course.id == 44 || course.id == 47) {
            course.icon = CourseIcons.Comments
          } else if (course.id == 48) {
            course.icon = CourseIcons.Abacus
          }
        }
        return course
      }
    })
  }

  private filterSelectedCoursesByAvailableCourses() {
    this.selectedCourseIds = this.selectedCourseIds.filter(courseId => this.availableCourses.find(course => course.id === courseId))
    this.selectedCourses = this.selectedCourses.filter(course => this.availableCourses.find(availableCourse => availableCourse.id === course.id))
  }

  private createAvailableCourses(allCourses: Array<Course>, newCourses: Array<Course>) {
    newCourses.forEach(newCourse => {
      const i = allCourses.findIndex(course => course.id === newCourse.id)
      if (i === -1) {
        allCourses.push(newCourse)
      }
    })
    allCourses.sort((a, b) => a.name.localeCompare(b.name))
  }

  private sortSchoolLevelsForGermany() {
    if (!this.schoolTypes) {
      return;
    }

    this.schoolTypes.forEach(schoolType => {
      const index = schoolType.levels.findIndex(level => level.id === 14);
      if (index > -1 && index !== 1) {
        const [level] = schoolType.levels.splice(index, 1);
        schoolType.levels.splice(1, 0, level);
      }
    });
  }
}